import { useEffect } from 'react';

const queryStringToLinks: string = '#cmpbox .cmpboxbtns .cmpmore > a';

/**
 * Custom hook to fix URL links within a specific CMP (Consent Management Platform) popup
 * by ensuring hash fragments are preceded by a '?' in the URL query string.
 * It uses an interval-based approach to wait for the elements to be present in the DOM.
 */
const useCmpPopupFix = (): void => {
  useEffect(() => {
    let intervalId: ReturnType<typeof setInterval> | undefined;

    const getCleanQueryWithHash = (href: string): string => {
      if (href.indexOf('?') === -1) {
        return href.replace('#', '?#');
      }
      return href;
    };

    const changeUrlLinks = (shouldApply: boolean): void => {
      if (!shouldApply) return;

      const elements: NodeListOf<HTMLAnchorElement> = document.querySelectorAll<HTMLAnchorElement>(queryStringToLinks);

      if (!elements.length) return;

      elements.forEach((element: HTMLAnchorElement) => {
        const href: string | null = element.getAttribute('href');
        if (href) {
          element.setAttribute('href', getCleanQueryWithHash(href));
          element.addEventListener('click', checkAndFix);
        }
      });
    };

    const checkAndFix = (): void => {
      waitForElement(queryStringToLinks, changeUrlLinks);
    };

    const waitForElement = (
      selector: string,
      callback: (found: boolean) => void,
      maxCount: number = 100,
      interval: number = 150,
    ): void => {
      let currentCount: number = 0;

      intervalId = setInterval(() => {
        currentCount++;

        if (currentCount === maxCount) {
          clearInterval(intervalId);
          callback(false);
          return;
        }

        const found: NodeListOf<Element> = document.querySelectorAll(selector);

        if (found.length > 0) {
          clearInterval(intervalId);
          callback(true);
        }
      }, interval);
    };

    checkAndFix();

    return () => {
      if (intervalId !== undefined) {
        clearInterval(intervalId);
      }

      // Remove click listeners to prevent memory leaks
      const elements: NodeListOf<HTMLAnchorElement> = document.querySelectorAll<HTMLAnchorElement>(queryStringToLinks);

      elements.forEach((el: HTMLAnchorElement) => {
        el.removeEventListener('click', checkAndFix);
      });
    };
  }, []);
};

export default useCmpPopupFix;
